import { KycAnalyzeIllustration, KycAnalyzeFailedIllustration } from '../../../../../../assets/illustrations';
import { CameraIcon, PaperclipIcon } from '../../../../../../assets/icons/common';
import { getProfileDataThunk } from '../../../../../../store/workspace/thunks';
import { objectURLToFile } from '../../../../../../utils/generalHelpers';
import { uploadFileThunk } from '../../../../../../store/general/thunks';
import { useDispatch, useSelector } from '../../../../../../store/store';
import { selectUserId } from '../../../../../../store/auth/selectors';
import { kycThunk } from '../../../../../../store/exam/thunks';
import { useWebcam } from '../../../../../../hooks/useWebcam';
import Spinner from '../../../../../../components/Spinner';
import DropBox from '../../../../../../containers/DropBox';
import Button from '../../../../../../components/Button';
import Modal from '../../../../../../components/Modal';
import { useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import styles from './style.module.scss';

const faceSample = require('../../../../../../assets/images/kyc_personal.png');
const idSample = require('../../../../../../assets/images/kyc_id.png');

type Mode = 'Camera' | 'Upload';

interface Mismatch {
    type: string;
    confidence: number;
    value: string | null;
};

const videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: 'user',
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function KYCWizardModal({ isOpen, onClose }: Props) {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [status, setStatus] = useState<'Pending' | 'Reject' | 'Approve'>('Pending');

    // Face state
    const [faceMode, setFaceMode] = useState<Mode>('Camera');
    const [faceFile, setFaceFile] = useState<File | null>(null);
    const { imgSrc: faceImgSrc, webcamRef: faceWebcamRef, setImgSrc: setFaceImgSrc, captureImage: captureFaceImage } = useWebcam();
    const [faceObjectURL, setFaceObjectURL] = useState<string | null>(null);

    // ID state
    const [idMode, setIdMode] = useState<Mode>('Camera');
    const [idFile, setIdFile] = useState<File | null>(null);
    const { imgSrc: idImgSrc, webcamRef: idWebcamRef, setImgSrc: setIdImgSrc, captureImage: captureIdImage } = useWebcam();
    const [idObjectURL, setIdObjectURL] = useState<string | null>(null);

    // Mismatches
    const [mismatches, setMismatches] = useState<Mismatch[]>([]);

    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setStatus('Pending');
            setFaceMode('Camera');
            setFaceFile(null);
            setFaceImgSrc(null);
            setFaceObjectURL(null);
            setIdMode('Camera');
            setIdFile(null);
            setIdImgSrc(null);
            setIdObjectURL(null);
        }
    }, [isOpen]);

    const handleFaceNext = () => {
        if (faceMode === 'Upload' && faceFile) {
            setFaceObjectURL(URL.createObjectURL(faceFile));
        } else {
            setFaceObjectURL(faceImgSrc);
        }
        setStep(2);
    };

    const handleIdNext = () => {
        if (idMode === 'Upload' && idFile) {
            setIdObjectURL(URL.createObjectURL(idFile));
        } else {
            setIdObjectURL(idImgSrc);
        }
        setStep(3);
    };

    useEffect(() => {
        if (step === 3 && status === 'Pending') {
            submitKYC();
        }
    }, [step, status]);

    const submitKYC = async () => {
        try {
            if (!faceObjectURL || !idObjectURL) return;
            const PIF = await objectURLToFile(faceObjectURL, 'face.png', 'image/png');
            const IDF = await objectURLToFile(idObjectURL, 'id.png', 'image/png');

            const PIF_eTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/face.png`, file: PIF }))).payload;
            const IDF_eTag = (await dispatch(uploadFileThunk({ fileName: `${userId}/id.png`, file: IDF }))).payload;

            await dispatch(kycThunk({
                e_tags: [PIF_eTag, IDF_eTag]
            })).unwrap();

            setStatus('Approve');
            await dispatch(getProfileDataThunk());
            onClose();
        } catch (error: any) {
            setStatus('Reject');
            setMismatches(error?.mismatches || []);
        }
    };

    const startOver = () => {
        setStep(1);
        setStatus('Pending');
        setFaceFile(null);
        setFaceImgSrc(null);
        setFaceObjectURL(null);
        setIdFile(null);
        setIdImgSrc(null);
        setIdObjectURL(null);
    };

    if (!isOpen) return null;

    return (
        <Modal
            size="md"
            title="KYC Verification"
            closeFunc={onClose}
        >
            <div className={styles.wrapper}>
                {step === 1 && (
                    <>
                        <div className={styles.box}>
                            {faceMode === 'Camera' && <>
                                <span className={styles.box_title}>
                                    Please position your face within the frame
                                </span>
                                <div className={styles.sample}>
                                    <img src={faceSample} alt="sample" />
                                </div>
                                <div className={styles.camera}>
                                    {!faceImgSrc ? <Webcam
                                        width={1280}
                                        height={720}
                                        audio={false}
                                        ref={faceWebcamRef}
                                        disablePictureInPicture
                                        forceScreenshotSourceSize
                                        screenshotFormat="image/png"
                                        videoConstraints={videoConstraints}
                                    /> : <img src={faceImgSrc} alt="Captured face" />}
                                </div>
                                <div className={styles.actions}>
                                    <Button
                                        onClick={() => faceImgSrc ? setFaceImgSrc(null) : captureFaceImage()}
                                        icon={<CameraIcon />}
                                        type='Outline-Primary'
                                        className={styles.box_actions_btn}
                                        text={faceImgSrc ? 'Retake photo' : 'Take photo'}
                                    />
                                </div>
                            </>}
                            {faceMode === 'Upload' && <>
                                <span className={styles.box_title}>
                                    Please upload your face photo
                                </span>
                                <DropBox
                                    file={faceFile}
                                    uploading={false}
                                    setFile={setFaceFile}
                                    fileTypes={{ 'image': ['.PNG', '.JPEG', '.JPG'], }}
                                    description='PNG, JPEG or JPG , Maximum upload file size: 15MB'
                                />
                            </>}
                        </div>
                        <div className={styles.change_option}>
                            <span className={styles.change_option_text}>
                                {faceMode === 'Camera' ? ' Also you can upload the relevant documents' : 'Also you can take a live photo of your face'}
                            </span>
                            <div
                                className={styles.change_option_link}
                                onClick={() => {
                                    setFaceImgSrc(null);
                                    setFaceFile(null);
                                    setFaceMode(prev => prev === 'Camera' ? 'Upload' : 'Camera');
                                }}
                            >
                                {faceMode === 'Camera' ? <PaperclipIcon /> : <CameraIcon />}
                                <span>
                                    {faceMode === 'Camera' ? ' Upload document' : 'Take photo'}
                                </span>
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <Button
                                size='sm'
                                text='Cancel'
                                type='Outline-Dark'
                                className={styles.actions_btn}
                                onClick={onClose}
                            />
                            <Button
                                size='sm'
                                type='Primary'
                                text='Continue'
                                onClick={handleFaceNext}
                                disabled={!faceImgSrc && !faceFile}
                                className={styles.actions_btn}
                            />
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className={styles.box}>
                            {idMode === 'Camera' && <>
                                <span className={styles.box_title}>
                                    Please take a live photo of your ID or upload the relevant document
                                </span>
                                <div className={styles.sample}>
                                    <img src={idSample} alt="sample" />
                                </div>
                                <div className={styles.camera}>
                                    {!idImgSrc ? <Webcam
                                        width={1280}
                                        height={720}
                                        audio={false}
                                        ref={idWebcamRef}
                                        disablePictureInPicture
                                        forceScreenshotSourceSize
                                        screenshotFormat="image/png"
                                        videoConstraints={videoConstraints}
                                    /> : <img src={idImgSrc} alt="Captured ID" />}
                                </div>
                                <div className={styles.actions}>
                                    <Button
                                        onClick={() => idImgSrc ? setIdImgSrc(null) : captureIdImage()}
                                        type='Outline-Primary'
                                        icon={<CameraIcon />}
                                        className={styles.box_actions_btn}
                                        text={idImgSrc ? 'Retake photo' : 'Take photo'}
                                    />
                                </div>
                            </>}
                            {idMode === 'Upload' && <>
                                <span className={styles.box_title}>
                                    Please upload your identity document
                                </span>
                                <DropBox
                                    file={idFile}
                                    uploading={false}
                                    setFile={setIdFile}
                                    fileTypes={{ 'image': ['.PNG', '.JPEG', '.JPG'], }}
                                    description='PNG, JPEG or JPG , Maximum upload file size: 15MB'
                                />
                            </>}
                        </div>
                        <div className={styles.change_option}>
                            <span className={styles.change_option_text}>
                                {idMode === 'Camera' ? ' Also you can upload the relevant documents' : 'Also you can take a live photo of your ID'}
                            </span>
                            <div
                                className={styles.change_option_link}
                                onClick={() => {
                                    setIdImgSrc(null);
                                    setIdFile(null);
                                    setIdMode(prev => prev === 'Camera' ? 'Upload' : 'Camera');
                                }}
                            >
                                {idMode === 'Camera' ? <PaperclipIcon /> : <CameraIcon />}
                                <span>
                                    {idMode === 'Camera' ? ' Upload document' : 'Take photo'}
                                </span>
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <Button
                                size='sm'
                                text='Back'
                                type='Outline-Dark'
                                className={styles.actions_btn}
                                onClick={() => setStep(1)}
                            />
                            <Button
                                size='sm'
                                type='Primary'
                                text='Continue'
                                onClick={handleIdNext}
                                disabled={!idImgSrc && !idFile}
                                className={styles.actions_btn}
                            />
                        </div>
                    </>
                )}

                {step === 3 && (
                    <div className={styles.pending}>
                        {status === 'Pending' && <>
                            <KycAnalyzeIllustration />
                            <span className={styles.title}>
                                Analyzing KYC information
                            </span>
                            <span className={styles.text}>
                                We are analyzing your KYC information. Please wait a moment.
                            </span>
                            <Spinner />
                        </>}

                        {status === 'Reject' && <>
                            <KycAnalyzeFailedIllustration />
                            <span className={styles.title} data-is-error="true">
                                KYC was unsuccessful!
                            </span>
                            <span className={styles.text}>
                                Please ensure the accuracy of your information.
                            </span>

                            {mismatches.length > 0 && <div className={styles.mismatches}>
                                <span className={styles.mismatches_title}>Mismatch{mismatches.length > 1 ? 'es' : ''}:</span>
                                <ul>
                                    {mismatches?.map((item, index) => (
                                        <li className={styles.item} key={index}>
                                            <span className={styles.label}>{item.type}: </span>
                                            <span className={styles.value}>{item.value || 'N/A'}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>}
                            <div className={styles.actions}>
                                <Button
                                    text='Try again'
                                    type='Outline-Dark'
                                    onClick={startOver}
                                    className={styles.actions_btn}
                                />
                                <Button
                                    type='Primary'
                                    text='Verify later'
                                    className={styles.actions_btn}
                                    onClick={onClose}
                                />
                            </div>
                        </>}
                    </div>
                )}
            </div>
        </Modal>
    );
}
